import { map, first } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { StableBox } from '../_models/stableBox';
import {
  TStableBox,
  TStableBoxUI,
  TStableBoxPayload,
  TBreedOption,
} from '../_models/stableBox.types';

@Injectable()
export class StableBoxService {
  // Services
  private http = inject(HttpClient);
  private apiBaseUrl = environment.apiBaseUrl;
  stableBoxList: StableBox[];
  stableBoxList$: BehaviorSubject<Array<StableBox>> = new BehaviorSubject<Array<StableBox>>([]);
  stableBox$: BehaviorSubject<StableBox> = new BehaviorSubject<StableBox>(null);

  constructor() {}

  getStableBoxs(): Observable<StableBox[]> {
    return this.http.get<any>(this.apiBaseUrl + 'api/private/v1/stableboxes').pipe(
      map((response) => {
        this.stableBoxList = response;
        this.stableBoxList$.next(response);
        return response;
      })
    );
  }

  getStableBox(id: number): Observable<StableBox> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/stableboxes/' + id)
      .pipe(map((response) => response));
  }

  createStableBox(payload: TStableBoxPayload): Observable<TStableBox> {
    const stableBox: any = { ...payload };

    // Clean up properties that shouldn't be sent to the API
    delete stableBox.lastUpdated;
    delete stableBox.dateCreated;

    if (stableBox && stableBox.stable && stableBox.stable.id) {
      stableBox.stable = { id: stableBox.stable.id };
    }

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    stableBox.companyId = companySelected ? companySelected.id : null;

    return this.http
      .post(this.apiBaseUrl + 'api/private/v1/stableboxes/', stableBox)
      .pipe(map((response) => response));
  }

  updateStableBox(payload: TStableBoxPayload): Observable<StableBox> {
    const stableBox: any = { ...payload };

    // Clean up properties that shouldn't be sent to the API
    delete stableBox.lastUpdated;
    delete stableBox.dateCreated;

    if (stableBox && stableBox.stable && stableBox.stable.id) {
      stableBox.stable = { id: stableBox.stable.id };
    }

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    stableBox.companyId = companySelected ? companySelected.id : null;

    return this.http
      .put(this.apiBaseUrl + 'api/private/v1/stableboxes/' + stableBox.id, stableBox)
      .pipe(map((response) => response));
  }

  /**
   * @deprecated Use createStableBox() or updateStableBox() instead for better type safety
   * Legacy method that saves a stable box (create or update based on presence of id)
   */
  saveStableBox(stableBoxItem: StableBox | TStableBoxPayload): Observable<StableBox> {
    const stableBox: any = { ...stableBoxItem };

    // Clean up properties that shouldn't be sent to the API
    delete stableBox.lastUpdated;
    delete stableBox.dateCreated;
    if (stableBox && stableBox.stable && stableBox.stable.id) {
      stableBox.stable = { id: stableBox.stable.id };
    }
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    stableBox.companyId = companySelected ? companySelected.id : null;

    if (stableBox && stableBox.id) {
      return this.http
        .put(this.apiBaseUrl + 'api/private/v1/stableboxes/' + stableBox.id, stableBox)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post(this.apiBaseUrl + 'api/private/v1/stableboxes/', stableBox)
        .pipe(map((response) => response));
    }
  }

  deleteStableBox(stableBox: StableBox | TStableBoxUI): Observable<StableBox> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/stableboxes/' + stableBox.id).pipe(
      map(() => {
        return { id: stableBox.id } as StableBox;
      })
    );
  }

  getStableBoxById = (id: number): StableBox => {
    let item: StableBox;
    this.stableBoxList$
      .pipe(first((list: StableBox[]) => list && list.length > 0))
      .subscribe((list: StableBox[]) => {
        item = list.find((i) => i.id === id);
      });
    return item;
  };

  /**
   * Get all stable boxes that belong to a specific stable
   * @param stableId - The ID of the stable
   * @returns Array of StableBox objects belonging to the stable
   */
  getStableBoxesByStableId = (stableId: number): StableBox[] => {
    let items: StableBox[] = [];
    this.stableBoxList$
      .pipe(first((list: StableBox[]) => list && list.length > 0))
      .subscribe((list: StableBox[]) => {
        items = list.filter((box) => box && box.stable && box.stable.id === stableId);
      });
    return items;
  };

  checkIfAnimalIdExistInStableBox(animalId) {
    const tmpBox = [];
    this.stableBoxList.forEach((box) => {
      if (box && box.animals && box.animals.length > 0) {
        box.animals.forEach((a) => {
          if (a && a.id && a.id === animalId) {
            tmpBox.push(box);
          }
        });
      }
    });
    return tmpBox;
  }

  getAnimalsWellnessLevel(
    typeOfWellness,
    beddingType,
    permanentSize,
    animalsSum,
    bunkNumber,
    wateringType,
    wateringPlaceNumber,
    linearMetersSize,
    stage
  ) {
    let ratio = 0;
    if (typeOfWellness == 'overcrowding') {
      if (beddingType == 'permanent' && permanentSize != 0 && animalsSum != 0) {
        ratio = parseFloat(permanentSize) / animalsSum;
      }
      if (beddingType == 'bunk' && bunkNumber != 0 && animalsSum != 0) {
        ratio = parseInt(bunkNumber) / animalsSum;
      }
    } else if (typeOfWellness == 'water_avaible') {
      if (wateringType == 'linear_meter' && linearMetersSize != 0 && animalsSum != 0) {
        ratio = linearMetersSize / animalsSum;
      }
      if (wateringType == 'watering_place' && wateringPlaceNumber != 0 && animalsSum != 0) {
        ratio = parseInt(wateringPlaceNumber) / animalsSum;
      }
    }
    if (ratio == 0) {
      return null;
    }
    let level = 0;
    const secondCondition =
      typeOfWellness == 'overcrowding'
        ? beddingType == 'permanent'
          ? stage == 'lactation' || stage == 'dry'
            ? //// permanente : fase lattazione e Asciutte
              6
            : stage == 'heifers'
              ? //// permanente : fase manze
                3.5
              : //// permanente : fase Vitelli 0-6
                1.6
          : //// cuccette
            0.9
        : wateringType == 'watering_place'
          ? //// beverini
            0.048
          : //// Metri
            0.06;

    const thirdCondition =
      typeOfWellness == 'overcrowding'
        ? beddingType == 'permanent'
          ? stage == 'lactation' || stage == 'dry'
            ? //// permanente : fase lattazione e Asciutte
              7
            : stage == 'heifers'
              ? //// permanente : fase manze
                4.5
              : //// permanente : fase Vitelli 0-6
                1.7
          : //// cuccette
            1
        : wateringType == 'watering_place'
          ? //// beverini
            0.06
          : //// Metri
            0.07;

    if (ratio < secondCondition) {
      level = 1;
    } else if (ratio >= secondCondition && ratio < thirdCondition) {
      level = 2;
    } else if (ratio >= thirdCondition) {
      level = 3;
    }

    return level;
  }

  /**
   * Transforms domain StableBox data to UI-friendly format
   * - Converts string characterization to array for x-multiselect
   * - Converts string stageStatus to array for x-multiselect
   * - Enriches breed keys with full breed objects for p-multiselect
   */
  mapDomainToUI(domain: TStableBox | any, allBreeds: TBreedOption[]): TStableBoxUI {
    if (!domain) {
      return null;
    }

    // Transform characterization: string → [{ id: string }]
    let characterization: Array<{ id: string }> = [];
    if (domain.characterization) {
      if (typeof domain.characterization === 'string') {
        characterization = [{ id: domain.characterization }];
      } else if (Array.isArray(domain.characterization)) {
        // Already in array format
        characterization = [...domain.characterization];
      }
    }

    // Transform stageStatus: string → [{ id: string }]
    let stageStatus: Array<{ id: string }> | null = null;
    if (domain.stageStatus) {
      if (typeof domain.stageStatus === 'string') {
        stageStatus = [{ id: domain.stageStatus }];
      } else if (Array.isArray(domain.stageStatus)) {
        stageStatus = [...domain.stageStatus];
      }
    }

    // Transform breeds: string[] → full breed objects
    const breeds: TBreedOption[] = [];
    if (domain.breeds && domain.breeds.length > 0) {
      domain.breeds.forEach((breed: any) => {
        if (typeof breed === 'string') {
          // Domain format: string[] → find full breed objects
          const breedOption = allBreeds.find((b) => b.key === breed);
          if (breedOption) {
            breeds.push(breedOption);
          } else {
            console.warn(`Breed key "${breed}" not found in allBreeds list`);
          }
        } else if (breed && typeof breed === 'object' && 'key' in breed) {
          // Already full object
          breeds.push(breed);
        }
      });
    }

    return {
      ...domain,
      characterization,
      stageStatus,
      breeds,
    };
  }

  /**
   * Transforms UI form data to API payload (used for both create and update)
   * - Extracts string value from characterization array
   * - Extracts string value from stageStatus array
   * - Extracts breed keys from full breed objects
   */
  mapUIToPayload(ui: TStableBoxUI): TStableBoxPayload {
    if (!ui) {
      return null;
    }

    // Transform characterization: [{ id: string }] → string
    const characterization =
      ui.characterization && ui.characterization.length > 0 ? ui.characterization[0].id : null;

    // Transform stageStatus: [{ id: string }] → string
    const stageStatus = ui.stageStatus && ui.stageStatus.length > 0 ? ui.stageStatus[0].id : null;

    // Transform breeds: full objects → string[]
    const breeds = ui.breeds && ui.breeds.length > 0 ? ui.breeds.map((b) => b.key) : [];

    return {
      id: ui.id,
      name: ui.name,
      productionOrientation: ui.productionOrientation,
      stage: ui.stage,
      characterization,
      stageStatus,
      hasPaddock: ui.hasPaddock,
      size: ui.size ?? 0,
      sizeUnit: ui.sizeUnit,
      femaleAnimalsNum: ui.femaleAnimalsNum,
      maleAnimalsNum: ui.maleAnimalsNum,
      stable: ui.stable,
      breeds,
      companyId: ui.id ? undefined : JSON.parse(localStorage.getItem('companySelected'))?.id,
      typeOfBedding: ui.typeOfBedding,
      permanentSize: ui.permanentSize,
      permanentSizeUnit: ui.permanentSizeUnit,
      bunkNumber: ui.bunkNumber,
      typeOfWatering: ui.typeOfWatering,
      wateringPlaceNumber: ui.wateringPlaceNumber,
      wateringSize: ui.wateringSize,
      wateringSizeUnit: ui.wateringSizeUnit,
      troughLength: ui.troughLength,
      manureManagements: ui.manureManagements,
      animals: ui.animals?.map((a) => ({ id: a.id })),
    };
  }
}
